// Global Instructions Rule Applied!

import {
  FieldTypeEnum,
  SelectTypeEnum,
  FieldConfig,
  SelectFieldConfig,
  CityFieldConfig,
  CountryFieldConfig,
  ContactFieldConfig,
  TextFieldConfig,
  ValuationFieldConfig
} from '../../../core/components/InputManager/types.ts'
import { introducedByHelp, SOURCED_BY_HELP } from '../../../core/utils/teamContactsHelp.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { UserEntry, UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import type { CompanyTeamEntry } from '../../../state_manager/data_models/company_team_entry.ts'
import { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { RoundEntryFields } from '../../../state_manager/data_models/round_entry.ts'
import {
  RELATIONSHIP_CATEGORY_INTRODUCED_BY,
  RELATIONSHIP_CATEGORY_SOURCED_BY,
  ACTION_TYPE_UPSERT,
  ACTION_TYPE_DELETE,
  FIELD_KEY_PROG,
  FIELD_KEY_ACTION,
  FIELD_KEY_SEPARATOR,
  TABLE_CONTACT_ACCOUNTS,
  TABLE_CONTACTS,
  ACCOUNT_ROLE_CHOICE_PIPELINE_COMPANY,
  ACCOUNT_ROLE_CHOICE_PORTFOLIO_COMPANY,
  ACCOUNT_ROLE_CHOICE_LP
} from '../../../state_manager/globals.ts'
import { ContactsModelFields } from '../../../state_manager/data_models/contacts_model.ts'
import { ContactAccountsModelFields } from '../../../state_manager/data_models/contact_accounts_model.ts'
import { AccountRoleEntryFields } from '../../../state_manager/data_models/account_role_entry.ts'
import { UNKNOWN_CONTACT } from '../../../state_manager/constants/strings.ts'
import {
  LOOKUP_CATEGORY_INDUSTRY,
  LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA,
  LOOKUP_CATEGORY_FUNDING_STAGE,
  LOOKUP_CATEGORY_PIPELINE_STAGE,
  LOOKUP_CATEGORY_SECURITY_TYPE
} from '../../../state_manager/constants/lookupCategories.ts'
import {
  buildCriticalTechnologyAreaField,
  CRITICAL_TECHNOLOGY_AREA_COLUMN
} from '../../global_configs/criticalTechnologyAreas.tsx'

/**
 * Field configuration utilities for Company Drawer InputManager integration
 * Generates InputManager field configurations for different sections of the drawer
 */

/**
 * Generates field configurations for Company Information section
 */
export function getCompanyInfoFields(formData: any, helpers: any): FieldConfig[] {
  const { getLookupOptions, getRoleOptions, getInvestorClassificationOptions } = helpers
  const industryOptions = getLookupOptions ? getLookupOptions(LOOKUP_CATEGORY_INDUSTRY) : []
  const ctaOptions = getLookupOptions ? getLookupOptions(LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA) : []
  const roleOptions = getRoleOptions ? getRoleOptions() : []
  const investorClassificationOptions = getInvestorClassificationOptions ? getInvestorClassificationOptions() : []

  // Transform prog__account_roles from array of objects to array of IDs if needed
  const rolesRaw = formData.prog__account_roles || formData.role || []
  const roles = Array.isArray(rolesRaw)
    ? rolesRaw
        .map((role: any) => {
          // If it's already a string ID, return it
          if (typeof role === 'string') return role
          // If it's an object, extract account_role_choice_id
          if (role && typeof role === 'object' && role.account_roles__account_role_choice_id) {
            return role.account_roles__account_role_choice_id
          }
          return null
        })
        .filter((id: any): id is string => id !== null)
    : []

  // Check if industry is required based on roles
  const requiresIndustry =
    Array.isArray(roles) &&
    roles.some(
      (roleId: string) =>
        roleId === ACCOUNT_ROLE_CHOICE_PIPELINE_COMPANY || roleId === ACCOUNT_ROLE_CHOICE_PORTFOLIO_COMPANY
    )

  const shouldShowInvestorClassification = Array.isArray(roles) && roles.includes(ACCOUNT_ROLE_CHOICE_LP)

  const fields: FieldConfig[] = [
    // Company Name
    {
      key: AccountRecordFields.accounts__name,
      label: 'Name',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__name] || '',
      required: true,
      editable: true,
      autoFocus: true,
      patchEndpoint: 'accounts'
    },
    // Legal Name
    {
      key: AccountRecordFields.accounts__legal_name,
      label: 'Legal name',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__legal_name] || '',
      editable: true,
      patchEndpoint: 'accounts'
    },
    // Other Names (Aliases) - editable field
    {
      key: AccountRecordFields.accounts__aliases,
      label: 'Other names',
      type: FieldTypeEnum.TAGS,
      value: Array.isArray(formData[AccountRecordFields.accounts__aliases])
        ? formData[AccountRecordFields.accounts__aliases]
        : [],
      editable: true,
      patchEndpoint: 'accounts',
      valueTransformer: (value: any, currentRecord: any) => {
        // TagsInput provides array directly
        // Return API field name 'aliases' (not prefixed) and Redux field name for state update
        const aliasesArray = Array.isArray(value)
          ? value.filter((tag: any) => typeof tag === 'string' && tag.trim() !== '')
          : []
        return {
          aliases: aliasesArray, // API field name (no prefix)
          [AccountRecordFields.accounts__aliases]: aliasesArray // Redux field name for state update
        }
      }
    },
    // Roles (multi-select)
    {
      key: 'prog__account_roles',
      label: 'Role',
      type: FieldTypeEnum.SELECT,
      value: roles,
      editable: true,
      required: true,
      options: roleOptions,
      allowClear: true,
      mode: 'multiple',
      tagDisplay: true,
      patchEndpoint: 'accounts',
      valueTransformer: (value: any, currentRecord: any) => {
        // Transform array of role IDs to objects with actions
        const newRoleIds = Array.isArray(value) ? value : []
        const accountId = currentRecord?.[AccountRecordFields.accounts__id]

        // Get current roles from record
        const currentRoles = currentRecord?.[AccountRecordFields.prog__account_roles] || []
        const currentRoleMap = new Map<string, any>()

        // Build map of current roles by account_role_choice_id
        currentRoles.forEach((role: any) => {
          if (typeof role === 'object' && role[AccountRoleEntryFields.account_roles__account_role_choice_id]) {
            const roleChoiceId = role[AccountRoleEntryFields.account_roles__account_role_choice_id]
            currentRoleMap.set(roleChoiceId, role)
          } else if (typeof role === 'string') {
            // Legacy format: just the ID
            currentRoleMap.set(role, { [AccountRoleEntryFields.account_roles__account_role_choice_id]: role })
          }
        })

        // Build map of new roles
        const newRoleMap = new Map<string, boolean>()
        newRoleIds.forEach((roleId: string) => {
          if (roleId) {
            newRoleMap.set(roleId, true)
          }
        })

        // Determine actions for each role
        const rolesWithActions: any[] = []

        // Process new roles (insert or update)
        newRoleIds.forEach((roleChoiceId: string) => {
          if (!roleChoiceId) return

          const currentRole = currentRoleMap.get(roleChoiceId)
          const roleEntry: any = {
            [AccountRoleEntryFields.account_roles__account_role_choice_id]: roleChoiceId
          }

          if (currentRole && currentRole[AccountRoleEntryFields.account_roles__id]) {
            // Existing role - update action
            roleEntry[AccountRoleEntryFields.account_roles__id] = currentRole[AccountRoleEntryFields.account_roles__id]
            roleEntry[AccountRoleEntryFields.prog__account_roles__action] = 'update'
          } else {
            // New role - insert action (generate UUID on frontend)
            roleEntry[AccountRoleEntryFields.account_roles__id] = crypto.randomUUID()
            roleEntry[AccountRoleEntryFields.prog__account_roles__action] = 'insert'
          }

          rolesWithActions.push(roleEntry)
        })

        // Process removed roles (delete)
        currentRoleMap.forEach((currentRole: any, roleChoiceId: string) => {
          if (!newRoleMap.has(roleChoiceId)) {
            // Role was removed - delete action
            const roleId = currentRole[AccountRoleEntryFields.account_roles__id]
            if (roleId) {
              rolesWithActions.push({
                [AccountRoleEntryFields.account_roles__id]: roleId,
                [AccountRoleEntryFields.prog__account_roles__action]: 'delete'
              })
            }
          }
        })

        return {
          // Send as prog__account_roles array with actions
          [AccountRecordFields.prog__account_roles]: rolesWithActions
        }
      }
    } as SelectFieldConfig,
    ...(shouldShowInvestorClassification
      ? [
          {
            key: AccountRecordFields.accounts__investor_classification_id,
            label: 'Investor Classification',
            type: FieldTypeEnum.SELECT,
            value: formData[AccountRecordFields.accounts__investor_classification_id] || null,
            editable: true,
            options: investorClassificationOptions,
            allowClear: true,
            patchEndpoint: 'accounts',
            valueTransformer: (value: any) => {
              const normalizedValue =
                value === undefined || value === null || value === '' ? null : Number.parseInt(`${value}`, 10)
              return {
                investor_classification_id: Number.isNaN(normalizedValue) ? null : normalizedValue,
                [AccountRecordFields.accounts__investor_classification_id]: Number.isNaN(normalizedValue)
                  ? null
                  : normalizedValue
              }
            }
          } as SelectFieldConfig
        ]
      : []),
    // Industry (conditionally required)
    {
      key: AccountRecordFields.accounts__industry,
      label: 'Industry',
      type: FieldTypeEnum.SELECT,
      value: formData[AccountRecordFields.accounts__industry] || '',
      editable: true,
      required: requiresIndustry,
      selectType: SelectTypeEnum.LOOKUP,
      lookupCategory: LOOKUP_CATEGORY_INDUSTRY,
      options: industryOptions,
      allowClear: true,
      patchEndpoint: 'accounts',
      valueTransformer: (value: any) => {
        // SelectInput returns UUID directly for lookup fields
        // Ensure we return null for empty values, otherwise return the UUID
        if (!value || value === '' || (typeof value === 'string' && value.trim() === '')) {
          return {
            industry: null,
            [AccountRecordFields.accounts__industry]: null
          }
        }
        // Value is already a UUID from SelectInput
        return {
          industry: value,
          [AccountRecordFields.accounts__industry]: value
        }
      }
    } as SelectFieldConfig,
    // Critical Technology Area — immediately below Industry (owner instruction
    // 2026-09-16). Never required: an SBIC-fund investment may fall under none.
    buildCriticalTechnologyAreaField({
      options: ctaOptions,
      value: formData[AccountRecordFields.accounts__critical_technology_area],
      key: AccountRecordFields.accounts__critical_technology_area,
      patchEndpoint: 'accounts',
      valueTransformer: (value: any) => {
        const next = !value || (typeof value === 'string' && value.trim() === '') ? null : value
        return {
          [CRITICAL_TECHNOLOGY_AREA_COLUMN]: next,
          [AccountRecordFields.accounts__critical_technology_area]: next
        }
      }
    }),
    // Website
    {
      key: AccountRecordFields.accounts__website,
      label: 'Website',
      type: FieldTypeEnum.URL,
      value: formData[AccountRecordFields.accounts__website] || '',
      editable: true,
      patchEndpoint: 'accounts'
    },
    // Domain
    {
      key: AccountRecordFields.accounts__domain,
      label: 'Domain',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__domain] || '',
      editable: true,
      patchEndpoint: 'accounts'
    },
    // Company Description
    {
      key: AccountRecordFields.accounts__description,
      label: 'Description',
      type: FieldTypeEnum.TEXTAREA,
      value: formData[AccountRecordFields.accounts__description] || '',
      editable: true,
      rows: 15,
      patchEndpoint: 'accounts'
    }
  ]

  return fields
}

/**
 * Generates field configurations for Company Details section (Details Tab only)
 * Includes all company info fields plus Incorporation, City Founded, and Country Founded
 */
export function getCompanyDetailsFields(formData: any, helpers: any): FieldConfig[] {
  // Get base company info fields
  const baseFields = getCompanyInfoFields(formData, helpers)

  // Get countriesData from helpers for country name lookup
  const countriesData = helpers?.countriesData || []

  const { getShareRegisterOptions } = helpers
  const shareRegisterOptions = getShareRegisterOptions ? getShareRegisterOptions() : []

  // Determine if this account is a Portfolio Company (drives share-register visibility)
  const rolesRaw = formData.prog__account_roles || formData.role || []
  const roles = Array.isArray(rolesRaw)
    ? rolesRaw
        .map((role: any) => {
          if (typeof role === 'string') return role
          if (role && typeof role === 'object' && role.account_roles__account_role_choice_id) {
            return role.account_roles__account_role_choice_id
          }
          return null
        })
        .filter((id: any): id is string => id !== null)
    : []
  const isPortfolioCompany = roles.includes(ACCOUNT_ROLE_CHOICE_PORTFOLIO_COMPANY)

  // Add Details Tab specific fields after Description
  const detailsFields: FieldConfig[] = [
    // Incorporation
    {
      key: AccountRecordFields.accounts__incorporation,
      label: 'Incorporation',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__incorporation] || '',
      editable: true,
      patchEndpoint: 'accounts'
    },
    // Share Register (Portfolio Companies only)
    ...(isPortfolioCompany
      ? [
          {
            key: AccountRecordFields.accounts__share_register_id,
            label: 'Share Register',
            type: FieldTypeEnum.SELECT,
            value: formData[AccountRecordFields.accounts__share_register_id] || null,
            editable: true,
            options: shareRegisterOptions,
            allowClear: true,
            patchEndpoint: 'accounts',
            valueTransformer: (value: any) => {
              const normalized = value === undefined || value === null || value === '' ? null : value
              return {
                share_register_id: normalized,
                [AccountRecordFields.accounts__share_register_id]: normalized
              }
            }
          } as SelectFieldConfig
        ]
      : []),
    // City Founded
    {
      key: AccountRecordFields.accounts__founded_in_city_label,
      label: 'City Founded',
      type: FieldTypeEnum.CITY,
      value: formData[AccountRecordFields.accounts__founded_in_city_label] || '',
      editable: true,
      country: formData[AccountRecordFields.accounts__founded_in_country_code] || '',
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'founded_in_city_code' },
        { sourceKey: 'cityName', targetField: 'founded_in_city_label' },
        { sourceKey: 'countryCode', targetField: 'founded_in_country_code' },
        { sourceKey: 'state', targetField: 'founded_in_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (cityObject: any) => {
        const cityCode = cityObject?.value
        const cityName = cityObject?.name || cityObject?.label || ''
        const countryCode = cityObject?.data?.countryCode || cityObject?.countryCode
        const state = cityObject?.data?.state ?? null

        if (!cityCode) {
          return {
            founded_in_city_code: null,
            founded_in_city_label: null,
            founded_in_stateprovince: null,
            [AccountRecordFields.accounts__founded_in_city_code]: null,
            [AccountRecordFields.accounts__founded_in_city_label]: null,
            [AccountRecordFields.accounts__founded_in_stateprovince]: null
          }
        }

        // Return full city object for Redux state and API field names for patch
        // IMPORTANT: Must return cityCode, cityName, countryCode, state to match patchFields sourceKeys
        return {
          // API fields (mapped by patchFields) - MUST match patchFields sourceKeys
          cityCode: cityCode,
          cityName: cityName,
          countryCode: countryCode || formData[AccountRecordFields.accounts__founded_in_country_code] || null,
          state: state,
          // Redux fields (direct mapping)
          [AccountRecordFields.accounts__founded_in_city_code]: cityCode,
          [AccountRecordFields.accounts__founded_in_city_label]: cityObject, // Full object for Redux
          [AccountRecordFields.accounts__founded_in_stateprovince]: state,
          // Set country code - CountrySelect will resolve the name from the code via lookupCountryByCode
          // The code is what gets stored in the database, CountrySelect handles display
          [AccountRecordFields.accounts__founded_in_country_code]:
            countryCode || formData[AccountRecordFields.accounts__founded_in_country_code] || null
        }
      }
    } as CityFieldConfig,
    // State / Province Founded
    {
      key: AccountRecordFields.accounts__founded_in_stateprovince,
      label: 'State / Province Founded',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__founded_in_stateprovince] || '',
      editable: true,
      patchEndpoint: 'accounts'
    },
    // Country Founded
    {
      key: AccountRecordFields.accounts__founded_in_country_code,
      label: 'Country Founded',
      type: FieldTypeEnum.COUNTRY,
      value: formData[AccountRecordFields.accounts__founded_in_country_code] || '',
      editable: true,
      patchEndpoint: 'accounts',
      valueTransformer: (countryValue: any) => {
        const countryCode = countryValue?.value || countryValue
        const countryLabel = countryValue?.label || countryValue

        if (!countryCode) {
          return {
            founded_in_country_code: null,
            founded_in_city_code: null,
            founded_in_city_label: null,
            founded_in_stateprovince: null,
            [AccountRecordFields.accounts__founded_in_country_code]: null,
            [AccountRecordFields.accounts__founded_in_city_code]: null,
            [AccountRecordFields.accounts__founded_in_city_label]: null,
            [AccountRecordFields.accounts__founded_in_stateprovince]: null
          }
        }

        return {
          founded_in_country_code: countryCode,
          founded_in_city_code: null, // Clear city when country changes
          founded_in_city_label: null, // Clear city when country changes
          founded_in_stateprovince: null, // Clear state when country changes
          [AccountRecordFields.accounts__founded_in_country_code]: countryCode,
          [AccountRecordFields.accounts__founded_in_city_code]: null,
          [AccountRecordFields.accounts__founded_in_city_label]: null,
          [AccountRecordFields.accounts__founded_in_stateprovince]: null
        }
      }
    } as CountryFieldConfig
  ]

  // Find the index of Description field to insert new fields after it
  const descriptionIndex = baseFields.findIndex((field) => field.key === AccountRecordFields.accounts__description)

  if (descriptionIndex >= 0) {
    // Insert new fields after Description
    return [...baseFields.slice(0, descriptionIndex + 1), ...detailsFields, ...baseFields.slice(descriptionIndex + 1)]
  }

  // If Description not found, just append to the end
  return [...baseFields, ...detailsFields]
}

/**
 * Generates field configurations for Business Address section
 */
export function getBusinessAddressFields(data: any, options: any, isReadOnly = false): FieldConfig[] {
  return [
    // Street address 1
    {
      key: AccountRecordFields.accounts__business_address_1,
      label: 'Street address 1',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__business_address_1] || '',
      editable: !isReadOnly
    },
    // Street address 2
    {
      key: AccountRecordFields.accounts__business_address_2,
      label: 'Street address 2',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__business_address_2] || '',
      editable: !isReadOnly
    },
    // City
    {
      key: AccountRecordFields.accounts__business_city_label,
      label: 'City',
      type: FieldTypeEnum.CITY,
      value: data[AccountRecordFields.accounts__business_city_label] || '',
      editable: !isReadOnly,
      country: data[AccountRecordFields.accounts__business_country_code] || '',
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'cityName', targetField: 'business_city_label' },
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (cityObject: any, currentRecord?: any) => {
        const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

        const cityCode = isClearing ? null : (cityObject?.value ?? null)
        const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null
        const countryCode = isClearing ? undefined : (cityObject?.data?.countryCode ?? undefined)
        const state = isClearing ? null : (cityObject?.data?.state ?? null) || null

        const result: Record<string, any> = {
          cityCode,
          cityName: cityLabel,
          state,
          [AccountRecordFields.accounts__business_city_label]: cityLabel,
          [AccountRecordFields.accounts__business_stateprovince]: state
        }

        if (!isClearing && countryCode !== undefined) {
          result.countryCode = countryCode
          result[AccountRecordFields.accounts__business_country_code] = countryCode
        }

        return result
      }
    } as CityFieldConfig,
    // State/Province
    {
      key: AccountRecordFields.accounts__business_stateprovince,
      label: 'State / Province',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__business_stateprovince] || '',
      editable: !isReadOnly
    },
    // ZIP code
    {
      key: AccountRecordFields.accounts__business_postalcode,
      label: 'ZIP code',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__business_postalcode] || '',
      editable: !isReadOnly
    },
    // Country
    {
      key: AccountRecordFields.accounts__business_country_label,
      label: 'Country',
      type: FieldTypeEnum.COUNTRY,
      value: data[AccountRecordFields.accounts__business_country_label] || '',
      editable: !isReadOnly,
      patchFields: [
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (countryObject: any) => {
        const isClearing =
          !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
        const countryCode = isClearing ? null : (countryObject?.value ?? null)
        const countryLabel = isClearing ? null : (countryObject?.label ?? null)

        return {
          countryCode,
          cityCode: null,
          state: null,
          [AccountRecordFields.accounts__business_country_code]: countryCode,
          [AccountRecordFields.accounts__business_country_label]: countryLabel,
          [AccountRecordFields.accounts__business_city_code]: null,
          [AccountRecordFields.accounts__business_city_label]: null,
          [AccountRecordFields.accounts__business_stateprovince]: null
        }
      }
    } as CountryFieldConfig
  ]
}

/**
 * Generates field configurations for Office Address section
 */
export function getOfficeAddressFields(data: any, options: any, isReadOnly = false): FieldConfig[] {
  // Get countriesData from options/helpers (matching business address pattern)
  const countriesData = options?.countriesData || []

  // Initialize city value - check for city object first (matching business address pattern)
  // Business address uses AccountRecordFields.accounts__business_city_label which stores the city object
  // Office address should use AccountRecordFields.accounts__office_city_code__label for the same purpose
  let officeCityValue = data[AccountRecordFields.accounts__office_city_code__label] || ''
  const officeCityCode = data[AccountRecordFields.accounts__office_city_code]
  const officeCountryCode = data[AccountRecordFields.accounts__office_country_code] || ''

  // If we have a city code but no city object/value, use the code directly
  // CitySelect component can handle numeric codes - it will show "City ID: X" until resolved
  // When user selects a city, the full object will be stored via handleFieldChange
  // This matches how business address works - if the _label field has a city object, use it; otherwise use code
  if (officeCityCode && !officeCityValue) {
    // Use city code directly - CitySelect will handle it
    // Note: This will show "City ID: X" until the city is selected again, but that's expected
    // since the backend doesn't provide the city name for office address
    officeCityValue = officeCityCode
  }

  // Initialize country value - resolve name from code if needed (matching business address pattern)
  let officeCountryValue = data[AccountRecordFields.accounts__office_country_code__label] || ''

  // If we have a country code but no country name, look it up from countriesData (matching business address pattern)
  if (officeCountryCode && !officeCountryValue && countriesData.length > 0) {
    const countryName = countriesData.find((country: any) => country.code === officeCountryCode)?.name
    if (countryName) {
      officeCountryValue = countryName
    } else {
      // Fallback to code if name not found
      officeCountryValue = officeCountryCode
    }
  } else if (officeCountryCode && !officeCountryValue) {
    // No countriesData available, use code as fallback
    officeCountryValue = officeCountryCode
  }

  return [
    // Street address 1
    {
      key: AccountRecordFields.accounts__office_address_1,
      label: 'Street address 1',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__office_address_1] || '',
      editable: !isReadOnly
    },
    // Street address 2
    {
      key: AccountRecordFields.accounts__office_address_2,
      label: 'Street address 2',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__office_address_2] || '',
      editable: !isReadOnly
    },
    // City
    {
      key: AccountRecordFields.accounts__office_city_code__label,
      label: 'City',
      type: FieldTypeEnum.CITY,
      value: officeCityValue,
      editable: !isReadOnly,
      country: officeCountryCode,
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'office_city_code' },
        { sourceKey: 'cityName', targetField: 'office_city_name' },
        { sourceKey: 'countryCode', targetField: 'office_country_code' },
        { sourceKey: 'state', targetField: 'office_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (cityObject: any, currentRecord?: any) => {
        const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

        const cityCode = isClearing ? null : (cityObject?.value ?? null)
        const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null
        const countryCode = isClearing ? undefined : (cityObject?.data?.countryCode ?? undefined)
        const state = isClearing ? null : (cityObject?.data?.state ?? null) || null

        const fullCityObject = isClearing
          ? null
          : typeof cityObject === 'object' && cityObject !== null
            ? cityObject
            : { value: cityCode, name: cityLabel, label: cityLabel, data: { countryCode } }

        const result: Record<string, any> = {
          cityCode,
          cityName: cityLabel,
          state,
          [AccountRecordFields.accounts__office_city_code__label]: fullCityObject,
          [AccountRecordFields.accounts__office_stateprovince]: state
        }

        if (!isClearing && countryCode !== undefined) {
          result.countryCode = countryCode
          result[AccountRecordFields.accounts__office_country_code] = countryCode
        }

        return result
      }
    } as CityFieldConfig,
    // State/Province
    {
      key: AccountRecordFields.accounts__office_stateprovince,
      label: 'State / Province',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__office_stateprovince] || '',
      editable: !isReadOnly
    },
    // ZIP code
    {
      key: AccountRecordFields.accounts__office_postalcode,
      label: 'ZIP code',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__office_postalcode] || '',
      editable: !isReadOnly
    },
    // Country
    {
      key: AccountRecordFields.accounts__office_country_code__label,
      label: 'Country',
      type: FieldTypeEnum.COUNTRY,
      value: officeCountryValue,
      editable: !isReadOnly,
      patchFields: [
        { sourceKey: 'countryCode', targetField: 'office_country_code' },
        { sourceKey: 'cityCode', targetField: 'office_city_code' },
        { sourceKey: 'state', targetField: 'office_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (countryObject: any) => {
        const isClearing =
          !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
        const countryCode = isClearing ? null : (countryObject?.value ?? null)
        const countryLabel = isClearing ? null : (countryObject?.label ?? null)

        return {
          countryCode,
          cityCode: null,
          state: null,
          [AccountRecordFields.accounts__office_country_code]: countryCode,
          [AccountRecordFields.accounts__office_country_code__label]: countryLabel,
          [AccountRecordFields.accounts__office_city_code]: null,
          [AccountRecordFields.accounts__office_city_code__label]: null,
          [AccountRecordFields.accounts__office_stateprovince]: null
        }
      }
    } as CountryFieldConfig
  ]
}

/**
 * Helper function to parse contact name into first and last name
 */
function parseContactName(name: string): { first_name: string | null; last_name: string } {
  if (!name || typeof name !== 'string') {
    return { first_name: null, last_name: '' }
  }
  const cleanName = name.trim()
  if (cleanName.length === 0) {
    return { first_name: null, last_name: '' }
  }
  const nameParts = cleanName.split(/\s+/).filter((part) => part.length > 0)

  if (nameParts.length === 0) {
    return { first_name: null, last_name: '' }
  }

  if (nameParts.length === 1) {
    return { first_name: null, last_name: nameParts[0] }
  }

  return {
    first_name: nameParts[0],
    last_name: nameParts.slice(1).join(' ')
  }
}

/**
 * Checks if a contact is new (not an existing contact)
 */
function isNewContact(contact: any): boolean {
  if (!contact) return false
  const contactValue = contact && typeof contact === 'object' ? contact.value : contact
  return contactValue === 'new' || contactValue === null || contactValue === undefined
}

/**
 * Helper function to transform contacts to user entries for contact_accounts relationships
 */
function transformContactsToUserEntries(
  currentContacts: any[],
  previousContacts: any[],
  relationshipCategoryId: string,
  isContactDeals: boolean
): any[] {
  const ALIAS_CONTACTS = `${TABLE_CONTACTS}${FIELD_KEY_SEPARATOR}`
  const ALIAS_CONTACT_ACCOUNTS = `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}`

  const FIELD_ID = `${ALIAS_CONTACTS}${ContactsModelFields.id}`
  const FIELD_EMAIL = `${ALIAS_CONTACTS}${ContactsModelFields.email}`
  const FIELD_FIRST_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.first_name}`
  const FIELD_LAST_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.last_name}`
  const FIELD_CONTACT_ACCOUNTS_ID = `${ALIAS_CONTACT_ACCOUNTS}${ContactAccountsModelFields.id}`
  const PROG_CONTACTS_ACTION = `${FIELD_KEY_PROG}${TABLE_CONTACTS}${FIELD_KEY_ACTION}`
  const PROG_ACTION = `${FIELD_KEY_PROG}${FIELD_KEY_ACTION}`

  const extractContactId = (c: any): string | null => {
    if (!c) return null
    if (typeof c === 'object') {
      return c.value || c.id || (typeof c === 'string' ? c : null)
    }
    if (typeof c === 'string') {
      return c
    }
    return null
  }

  const currentContactIds = new Set(
    (currentContacts || [])
      .map(extractContactId)
      .filter((id): id is string => id !== null && id !== undefined && id !== 'new')
  )
  const previousContactIds = new Set(
    (previousContacts || [])
      .map(extractContactId)
      .filter((id): id is string => id !== null && id !== undefined && id !== 'new')
  )

  const userEntries: any[] = []

  // Process current contacts (new and existing)
  ;(currentContacts || []).forEach((contact: any) => {
    const contactValue = contact && typeof contact === 'object' ? contact.value : contact
    const isNew = isNewContact(contact)

    const userEntry: any = {
      [PROG_ACTION]: ACTION_TYPE_UPSERT
    }

    if (isNew) {
      const contactId = crypto.randomUUID()
      const nameSource = contact.data?.originalSearch || contact.label || ''
      const { first_name, last_name } = parseContactName(nameSource)

      userEntry[FIELD_ID] = contactId
      userEntry[FIELD_FIRST_NAME] = first_name || null
      userEntry[FIELD_LAST_NAME] = last_name
      userEntry[FIELD_EMAIL] = contact.email || null
      userEntry[PROG_CONTACTS_ACTION] = ACTION_TYPE_UPSERT
    } else {
      userEntry[FIELD_ID] = contactValue
      userEntry[PROG_CONTACTS_ACTION] = ''
    }

    // Look up existing relationship ID from previousContacts before generating new UUID
    // This prevents creating duplicate relationships when re-adding existing contacts
    let relationshipId =
      contact.contact_accounts_id ||
      contact.data?.[UserEntryFields.contact_accounts__id] ||
      contact.data?.contact_accounts__id

    // If relationshipId not found in contact, look it up from previousContacts
    if (!relationshipId && !isNew && contactValue) {
      const previousContact = (previousContacts || []).find((c: any) => {
        if (!c) return false
        const prevContactId = extractContactId(c)
        return prevContactId === contactValue
      })

      if (previousContact) {
        relationshipId =
          previousContact.contact_accounts_id ||
          previousContact.data?.[UserEntryFields.contact_accounts__id] ||
          previousContact.data?.contact_accounts__id ||
          previousContact.data?.contact_accounts_id ||
          null
      }
    }

    // Only generate new UUID if contact is truly new (not found in previousContacts)
    userEntry[FIELD_CONTACT_ACCOUNTS_ID] = relationshipId || crypto.randomUUID()

    userEntries.push(userEntry)
  })

  // Process removed contacts
  previousContactIds.forEach((contactId) => {
    if (!currentContactIds.has(contactId) && contactId !== 'new' && contactId && typeof contactId === 'string') {
      const previousContact = (previousContacts || []).find((c: any) => {
        if (!c) return false
        if (typeof c === 'object') {
          return (c.value || c.id) === contactId
        }
        return c === contactId
      })

      const userEntry: any = {
        [FIELD_ID]: contactId,
        [PROG_CONTACTS_ACTION]: '',
        [PROG_ACTION]: ACTION_TYPE_DELETE
      }

      // Look up relationship ID from previousContact with multiple fallback checks
      const relationshipId =
        previousContact?.contact_accounts_id ||
        previousContact?.data?.[UserEntryFields.contact_accounts__id] ||
        previousContact?.data?.contact_accounts__id ||
        previousContact?.data?.contact_accounts_id ||
        null

      // Defensive check: warn if contact_accounts_id cannot be found for deletion
      if (!relationshipId) {
        console.warn(
          `[transformContactsToUserEntries] Missing contact_accounts__id for deleted contact. Contact ID: ${contactId}, Relationship Category: ${relationshipCategoryId}`,
          {
            previousContact,
            previousContactsLength: previousContacts?.length,
            contactId
          }
        )
        // Still add the delete entry but without relationship ID - backend may handle this
        // or we may need to skip it depending on backend requirements
      }

      userEntry[FIELD_CONTACT_ACCOUNTS_ID] = relationshipId

      // Only add delete entry if we have a relationship ID (backend requirement)
      // If relationship ID is missing, log error but don't add invalid entry
      if (relationshipId) {
        userEntries.push(userEntry)
      } else {
        console.error(
          `[transformContactsToUserEntries] Skipping delete entry for contact ${contactId} - missing contact_accounts__id. This may indicate a state synchronization issue.`
        )
      }
    }
  })

  return userEntries
}

/**
 * A sourcing contact (Introduced by / Sourced by) as the company form's General section holds it: the select
 * option plus the source entry it was built from. `contact_accounts_id` is the relationship row, when known.
 */
export interface SourcingContactOption {
  value: string
  label: string
  email: string
  contact_accounts_id?: string | null
  data: UserEntry | CompanyTeamEntry
}

/** The General section's selected sourcing contacts, keyed by role. */
export interface SourcingContactsByRole {
  introduced_by: SourcingContactOption[]
  sourced_by: SourcingContactOption[]
}

/**
 * Converts contact objects to SelectListEntry format for ContactInput options
 */
function convertContactsToOptions(contacts: any[]): SelectListEntry[] {
  if (!contacts || !Array.isArray(contacts)) {
    return []
  }

  return contacts.map((contact) => {
    const name = contact.label || contact.name || UNKNOWN_CONTACT
    const email = contact.email || ''
    const labelWithEmail = `${name}${email && email !== '' ? ` (${email.toLowerCase()})` : ' (No Email)'}`

    return {
      value: contact.value,
      label: labelWithEmail,
      name: name
    }
  })
}

/**
 * Generates field configurations for General section (introduced_by, sourced_by, description)
 * @param companyLookupId - Internal company ID for Sourced By contact filtering (restricts to internal contacts)
 */
export function getGeneralFields(
  formData: any,
  selectedContacts: any,
  previousContacts?: any,
  companyLookupId?: string | null
): FieldConfig[] {
  const introducedByOptions = convertContactsToOptions(selectedContacts.introduced_by || [])
  const sourcedByOptions = convertContactsToOptions(selectedContacts.sourced_by || [])

  const prevContacts = previousContacts || {
    introduced_by: [],
    sourced_by: []
  }

  return [
    // Introduced By
    {
      key: AccountRecordFields.contact_accounts__introduced_by,
      label: 'Introduced by',
      labelTooltip: introducedByHelp('company or investor'),
      type: FieldTypeEnum.CONTACT,
      value: selectedContacts.introduced_by || [],
      editable: true,
      mode: 'multiple',
      options: introducedByOptions,
      patchEndpoint: 'contact_accounts',
      valueTransformer: (value: any, currentRecord: any) => {
        const currentContacts = Array.isArray(value) ? value : []
        const previousContacts = prevContacts.introduced_by || []
        const userFields = transformContactsToUserEntries(
          currentContacts,
          previousContacts,
          RELATIONSHIP_CATEGORY_INTRODUCED_BY,
          false
        )
        const accountId =
          formData?.[AccountRecordFields.accounts__id] || currentRecord?.[AccountRecordFields.accounts__id] || null
        return {
          id: accountId,
          userFields,
          relationship_category_id: RELATIONSHIP_CATEGORY_INTRODUCED_BY
        }
      }
    } as ContactFieldConfig,
    // Sourced By
    {
      key: AccountRecordFields.contact_accounts__sourced_by,
      label: 'Sourced by',
      labelTooltip: SOURCED_BY_HELP,
      type: FieldTypeEnum.CONTACT,
      value: selectedContacts.sourced_by || [],
      editable: true,
      mode: 'multiple',
      companyId: companyLookupId ?? null,
      options: sourcedByOptions,
      patchEndpoint: 'contact_accounts',
      valueTransformer: (value: any, currentRecord: any) => {
        const currentContacts = Array.isArray(value) ? value : []
        const previousContacts = prevContacts.sourced_by || []
        const userFields = transformContactsToUserEntries(
          currentContacts,
          previousContacts,
          RELATIONSHIP_CATEGORY_SOURCED_BY,
          false
        )
        const accountId =
          formData?.[AccountRecordFields.accounts__id] || currentRecord?.[AccountRecordFields.accounts__id] || null
        return {
          id: accountId,
          userFields,
          relationship_category_id: RELATIONSHIP_CATEGORY_SOURCED_BY
        }
      }
    } as ContactFieldConfig
  ]
}

/**
 * Generates field configurations for Round Overview section
 * Uses VALUATION field type to show single label with both Pre/Post fields in edit mode
 */
export function getRoundOverviewFields(data: any, helpers: any, isReadOnly = true): FieldConfig[] {
  const { getCurrencyById } = helpers
  // Valuation comes from prog__recent_round (requires both date and valuation)
  const recentRound = data?.[AccountRecordFields.prog__recent_round] || null
  // Round name and date come from prog__latest_round (most recent with date)
  const latestRound = data?.[AccountRecordFields.prog__latest_round] || null

  // Use recentRound for valuation fields, fallback to latestRound, then fallback to first round in array
  let roundForValuation = recentRound || latestRound
  if (!roundForValuation) {
    const roundsArray = data?.[AccountRecordFields.prog__rounds] || []
    if (Array.isArray(roundsArray) && roundsArray.length > 0) {
      roundForValuation = roundsArray[0]
    }
  }

  const roundId = roundForValuation?.[RoundEntryFields.rounds__id] || null
  const currencyId = roundForValuation?.[RoundEntryFields.rounds__currency] || null
  const currencySymbol = getCurrencyById?.(currencyId)?.symbol || '$'

  const fields: FieldConfig[] = []

  // Valuation field - shows single label, but both Pre/Post fields in edit mode
  const valuationField: ValuationFieldConfig = {
    key: RoundEntryFields.prog__valuation,
    label: 'Valuation',
    type: FieldTypeEnum.VALUATION,
    value: {
      preMoneyValue: roundForValuation?.[RoundEntryFields.rounds__pre_money_valuation] || null,
      postMoneyValue: roundForValuation?.[RoundEntryFields.rounds__post_money_valuation] || null
    },
    editable: !isReadOnly && !!roundId,
    currencySymbol: currencySymbol,
    preMoneyValue: roundForValuation?.[RoundEntryFields.rounds__pre_money_valuation] || null,
    postMoneyValue: roundForValuation?.[RoundEntryFields.rounds__post_money_valuation] || null,
    min: 0,
    step: 0.01,
    showValuationType: true
  }

  // Add patching configuration when editable
  if (roundId && !isReadOnly) {
    ;(valuationField as any).patchEndpoint = 'rounds'
    ;(valuationField as any).patchFields = [
      { sourceKey: 'preMoneyValue', targetField: 'pre_money_valuation' },
      { sourceKey: 'postMoneyValue', targetField: 'post_money_valuation' }
    ]
    ;(valuationField as any).valueTransformer = (value: any, currentRecord: any) => {
      // Return source keys that patchFields expects, plus Redux field names
      const result: any = {
        // Source keys for patchFields mapping
        preMoneyValue: value?.preMoneyValue || null,
        postMoneyValue: value?.postMoneyValue || null,
        // Redux field names for state updates
        [RoundEntryFields.rounds__pre_money_valuation]: value?.preMoneyValue || null,
        [RoundEntryFields.rounds__post_money_valuation]: value?.postMoneyValue || null
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  }

  fields.push(valuationField)

  // Latest Round Name - use roundForValuation which includes fallback logic
  const roundNameField: TextFieldConfig = {
    key: RoundEntryFields.rounds__name,
    label: 'Round',
    type: FieldTypeEnum.TEXT,
    value: roundForValuation?.[RoundEntryFields.rounds__name] || '',
    editable: !isReadOnly && !!roundId,
    hyperlinked: !!roundId,
    hyperlinkModule: 'rounds',
    getRecordId: () => roundId,
    ...(roundId && !isReadOnly
      ? {
          patchEndpoint: 'rounds',
          valueTransformer: (value, currentRecord) => {
            const result: any = {
              name: value,
              [RoundEntryFields.rounds__name]: value
            }
            if (roundId) {
              result.id = roundId
            }
            return result
          }
        }
      : {})
  }

  // Add custom hyperlink handler if provided
  if (helpers?.onRoundHyperlinkClick && roundId) {
    roundNameField.onHyperlinkClick = helpers.onRoundHyperlinkClick
  }

  fields.push(roundNameField)

  // Latest Round Date - use roundForValuation which includes fallback logic
  fields.push({
    key: RoundEntryFields.rounds__round_close_date,
    label: 'Date',
    type: FieldTypeEnum.DATE,
    value: roundForValuation?.[RoundEntryFields.rounds__round_close_date] || null,
    editable: !isReadOnly && !!roundId,
    ...(roundId && !isReadOnly
      ? {
          patchEndpoint: 'rounds',
          valueTransformer: (value, currentRecord) => {
            const result: any = {
              round_close_date: value,
              [RoundEntryFields.rounds__round_close_date]: value
            }
            if (roundId) {
              result.id = roundId
            }
            return result
          }
        }
      : {})
  })

  return fields
}

/**
 * Generates field configurations for a single round row in FundingRoundsTable
 * Includes Pre-money and Post-money valuation fields following deals pattern
 */
export function getRoundFields(roundData: any, helpers: any, isReadOnly = false): FieldConfig[] {
  const { getCurrencyById, getLookupOptions } = helpers
  const currencyId = roundData[RoundEntryFields.rounds__currency] || null
  const roundId = roundData[RoundEntryFields.rounds__id] || null
  const currencySymbol = getCurrencyById?.(currencyId)?.symbol || '$'
  const fundingStageOptions = getLookupOptions ? getLookupOptions(LOOKUP_CATEGORY_FUNDING_STAGE) : []

  const fields: FieldConfig[] = []

  // Round Name
  fields.push({
    key: RoundEntryFields.rounds__name,
    label: 'Name',
    type: FieldTypeEnum.TEXT,
    value: roundData[RoundEntryFields.rounds__name] || '',
    editable: !isReadOnly && !!roundId,
    patchEndpoint: 'rounds',
    hyperlinked: true,
    hyperlinkModule: 'rounds',
    getRecordId: () => roundId || null,
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        name: value,
        [RoundEntryFields.rounds__name]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // Round Date
  fields.push({
    key: RoundEntryFields.rounds__round_close_date,
    label: 'Date',
    type: FieldTypeEnum.DATE,
    value: roundData[RoundEntryFields.rounds__round_close_date] || null,
    editable: !isReadOnly && !!roundId,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        round_close_date: value,
        [RoundEntryFields.rounds__round_close_date]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // Pre-money Valuation
  fields.push({
    key: RoundEntryFields.rounds__pre_money_valuation,
    label: 'Pre-money Valuation',
    type: FieldTypeEnum.CURRENCY,
    value: roundData[RoundEntryFields.rounds__pre_money_valuation] || null,
    editable: !isReadOnly && !!roundId,
    currencySymbol: currencySymbol,
    min: 0,
    step: 0.01,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const preMoneyValue = value !== null && value !== undefined ? value : null
      const postMoneyValue =
        currentRecord?.[RoundEntryFields.rounds__post_money_valuation] !== null &&
        currentRecord?.[RoundEntryFields.rounds__post_money_valuation] !== undefined
          ? currentRecord[RoundEntryFields.rounds__post_money_valuation]
          : roundData?.[RoundEntryFields.rounds__post_money_valuation] !== null &&
              roundData?.[RoundEntryFields.rounds__post_money_valuation] !== undefined
            ? roundData[RoundEntryFields.rounds__post_money_valuation]
            : null
      const result: any = {
        pre_money_valuation: preMoneyValue,
        post_money_valuation: postMoneyValue,
        [RoundEntryFields.rounds__pre_money_valuation]: preMoneyValue,
        [RoundEntryFields.rounds__post_money_valuation]: postMoneyValue
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // Post-money Valuation
  fields.push({
    key: RoundEntryFields.rounds__post_money_valuation,
    label: 'Post-money Valuation',
    type: FieldTypeEnum.CURRENCY,
    value: roundData[RoundEntryFields.rounds__post_money_valuation] || null,
    editable: !isReadOnly && !!roundId,
    currencySymbol: currencySymbol,
    min: 0,
    step: 0.01,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const postMoneyValue = value !== null && value !== undefined ? value : null
      const preMoneyValue =
        currentRecord?.[RoundEntryFields.rounds__pre_money_valuation] !== null &&
        currentRecord?.[RoundEntryFields.rounds__pre_money_valuation] !== undefined
          ? currentRecord[RoundEntryFields.rounds__pre_money_valuation]
          : roundData?.[RoundEntryFields.rounds__pre_money_valuation] !== null &&
              roundData?.[RoundEntryFields.rounds__pre_money_valuation] !== undefined
            ? roundData[RoundEntryFields.rounds__pre_money_valuation]
            : null
      const result: any = {
        pre_money_valuation: preMoneyValue,
        post_money_valuation: postMoneyValue,
        [RoundEntryFields.rounds__pre_money_valuation]: preMoneyValue,
        [RoundEntryFields.rounds__post_money_valuation]: postMoneyValue
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // Raise Amount (Size)
  fields.push({
    key: RoundEntryFields.rounds__raise_amount,
    label: 'Size',
    type: FieldTypeEnum.CURRENCY,
    value: roundData[RoundEntryFields.rounds__raise_amount] || null,
    editable: !isReadOnly && !!roundId,
    currencySymbol: currencySymbol,
    min: 0,
    step: 0.01,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        raise_amount: value,
        [RoundEntryFields.rounds__raise_amount]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // Funding Stage
  fields.push({
    key: RoundEntryFields.rounds__funding_stage,
    label: 'Stage',
    type: FieldTypeEnum.SELECT,
    value: roundData[RoundEntryFields.rounds__funding_stage] || '',
    editable: !isReadOnly && !!roundId,
    selectType: SelectTypeEnum.LOOKUP,
    lookupCategory: LOOKUP_CATEGORY_FUNDING_STAGE,
    options: fundingStageOptions,
    allowClear: true,
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        funding_stage: value,
        [RoundEntryFields.rounds__funding_stage]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  } as SelectFieldConfig)

  // Our Investment (Commitment)
  // Note: Commitment cannot be patched through rounds endpoint, so this field is read-only
  fields.push({
    key: RoundEntryFields.prog__commitment,
    label: 'Our Investment',
    type: FieldTypeEnum.CURRENCY,
    value: roundData[RoundEntryFields.prog__commitment] || null,
    editable: false, // Read-only - commitment cannot be patched through rounds endpoint
    currencySymbol: currencySymbol,
    min: 0,
    step: 0.01
    // No patchEndpoint - field is read-only
  })

  return fields
}
